import React, { useState } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import type { SxProps, Theme } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { SvgIcon, type SvgIconProps } from "@mui/material";
import { useHasPrivilege } from "../../hooks/useHasPrivilege";
import { useBrandThemeColors } from "../../branding";
import iconCompany from "../../assets/icon-company.svg";
import iconCustom from "../../assets/icon-custom.svg";

const RunAssessmentCustomIcon = (props: SvgIconProps) => (
  <SvgIcon width="12" height="12" viewBox="0 0 12 12" fill="none" {...props}>
    <path
      d="M5.32478 11.6811C5.19353 11.6811 5.08103 11.6623 4.94978 11.6061C4.59353 11.4373 4.40603 11.0623 4.48103 10.6686L5.04353 7.68732H1.61228C1.29353 7.68732 0.993533 7.49982 0.843533 7.21857C0.693533 6.93732 0.731033 6.58107 0.918533 6.31857L5.13728 0.674824C5.36228 0.374824 5.73728 0.262324 6.09353 0.374824C6.44978 0.487324 6.67478 0.806074 6.67478 1.18107V3.82482H10.3873C10.7248 3.82482 11.0248 4.01232 11.156 4.31232C11.306 4.61232 11.2498 4.96857 11.0435 5.21232L5.98103 11.3623C5.81228 11.5686 5.56853 11.6811 5.32478 11.6811ZM5.81228 1.18107L1.59353 6.82482L5.54978 6.84357C5.68103 6.84357 5.79353 6.89982 5.86853 6.99357C5.94353 7.08732 5.98103 7.21857 5.96228 7.34982L5.30603 10.8373L5.32478 10.8561L10.406 4.68732L6.26228 4.66857C6.03728 4.66857 5.83103 4.48107 5.83103 4.23732L5.81228 1.18107Z"
      fill="currentColor"
    />
  </SvgIcon>
);

/** Class for toolbar grey-button overrides to skip (contained primary trigger). */
export const RUN_AI_ASSESSMENT_BUTTON_CLASS = "run-ai-assessment-trigger";

export type RunAiAssessmentMenuButtonProps = Readonly<{
  onCompanyScopeImpact?: () => void;
  onCustomScopeImpact?: () => void;
  disabled?: boolean;
  /** While Company Scope preview / task APIs are in flight */
  companyScopeStarting?: boolean;
  /** Toolbar full label; compact smaller label; iconOnly = grid row; link = inline text CTA */
  variant?: "toolbar" | "compact" | "iconOnly" | "link";
  /** Copy for `variant="link"` (empty-state inline CTA) */
  linkLabel?: string;
  /** Merged into the primary Button `sx` */
  buttonSx?: SxProps<Theme>;
}>;

export const RunAiAssessmentMenuButton: React.FC<RunAiAssessmentMenuButtonProps> = ({
  onCompanyScopeImpact,
  onCustomScopeImpact,
  disabled = false,
  companyScopeStarting = false,
  variant = "toolbar",
  linkLabel = "Run AI assessment now",
  buttonSx,
}) => {
  const { primaryColor, primaryFontColor } = useBrandThemeColors();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const canRunSystemImpact = useHasPrivilege(
    "gri.assessment.impact.system.execute",
  );
  const showCompanyScope =
    canRunSystemImpact && Boolean(onCompanyScopeImpact);
  const showCustomScope = Boolean(onCustomScopeImpact);

  if (!showCompanyScope && !showCustomScope) {
    return null;
  }

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (
    _event: object,
    _reason?: "backdropClick" | "escapeKeyDown",
  ) => {
    setAnchorEl(null);
  };

  const isCompact = variant === "compact";

  const sizeButtonSx: SxProps<Theme> = isCompact
    ? {
        height: "28px",
        fontSize: "12px",
        px: 1,
        minWidth: 0,
        whiteSpace: "nowrap",
      }
    : {
        height: "32px",
        px: 1.5,
      };

  const menuPaperSx = {
    mt: 0.5,
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    minWidth: 220,
    "& .MuiMenuItem-root": {
      fontSize: "14px",
      py: 1,
    },
    "& .MuiListItemIcon-root": {
      minWidth: "32px !important",
      color: "#595959",
    },
  };

  const defaultIconOnlySx: SxProps<Theme> = {
    p: 0.5,
    width: 24,
    height: 24,
    color: "rgba(0, 0, 0, 0.54)",
    "&.Mui-disabled": {
      color: "rgba(0, 0, 0, 0.26)",
    },
  };

  const menu = (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      PaperProps={{
        sx: menuPaperSx,
      }}
    >
      {showCompanyScope ? (
        <MenuItem
          disabled={companyScopeStarting}
          onClick={() => {
            onCompanyScopeImpact?.();
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            {companyScopeStarting ? (
              <CircularProgress size={16} sx={{ color: "#595959" }} />
            ) : (
              <Box
                component="img"
                src={iconCompany}
                alt=""
                sx={{ width: 16, height: 16, display: "block" }}
              />
            )}
          </ListItemIcon>
          Company Scope Impact
        </MenuItem>
      ) : null}
      {showCustomScope ? (
        <MenuItem
          onClick={() => {
            onCustomScopeImpact?.();
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <Box
              component="img"
              src={iconCustom}
              alt=""
              sx={{ width: 16, height: 16, display: "block" }}
            />
          </ListItemIcon>
          Custom Scope Impact
        </MenuItem>
      ) : null}
    </Menu>
  );

  const triggerDisabled = disabled;

  const runAiAssessmentLinkSx: SxProps<Theme> = {
    color: primaryColor,
    fontSize: "inherit",
    lineHeight: "inherit",
    fontWeight: 400,
    verticalAlign: "baseline",
    textDecoration: "none",
    cursor: triggerDisabled ? "default" : "pointer",
    border: "none",
    background: "none",
    p: 0,
    fontFamily: "inherit",
    "&:hover": {
      textDecoration: triggerDisabled ? "none" : "underline",
    },
    "&.Mui-disabled": {
      color: "#9ca3af",
      textDecoration: "none",
    },
  };

  if (variant === "link") {
    return (
      <>
        <Link
          component="button"
          type="button"
          disabled={triggerDisabled}
          onClick={handleOpen}
          sx={[
            runAiAssessmentLinkSx,
            ...(buttonSx
              ? Array.isArray(buttonSx)
                ? buttonSx
                : [buttonSx]
              : []),
          ]}
        >
          {linkLabel}
        </Link>
        {menu}
      </>
    );
  }

  if (variant === "iconOnly") {
    return (
      <>
        <Tooltip title={triggerDisabled ? "" : "Run AI Assessment"}>
          <IconButton
            size="small"
            aria-label="run ai assessment"
            disabled={triggerDisabled}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleOpen}
            sx={[
              defaultIconOnlySx,
              ...(buttonSx
                ? Array.isArray(buttonSx)
                  ? buttonSx
                  : [buttonSx]
                : []),
            ]}
          >
            {companyScopeStarting ? (
              <CircularProgress size={14} sx={{ color: "rgba(0, 0, 0, 0.54)" }} />
            ) : (
              <RunAssessmentCustomIcon sx={{ fontSize: "1.1rem" }} />
            )}
          </IconButton>
        </Tooltip>
        {menu}
      </>
    );
  }

  return (
    <>
      <Button
        className={RUN_AI_ASSESSMENT_BUTTON_CLASS}
        variant="contained"
        size="small"
        disabled={triggerDisabled}
        startIcon={
          companyScopeStarting ? (
            <CircularProgress size={isCompact ? 12 : 14} sx={{ color: "#fff !important" }} />
          ) : (
            <RunAssessmentCustomIcon
              sx={{
                fontSize: isCompact ? "12px !important" : "14px !important",
                color: "#fff !important",
              }}
            />
          )
        }
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              fontSize: isCompact ? "14px !important" : "16px !important",
              color: "#fff !important",
            }}
          />
        }
        onClick={handleOpen}
        sx={[
          {
            bgcolor: `${primaryColor} !important`,
            color: `${primaryFontColor} !important`,
            boxShadow: "none",
            "&:hover": {
              bgcolor: `${primaryColor} !important`,
              boxShadow: "none",
            },
            textTransform: "none",
            fontWeight: 500,
            "& .MuiButton-startIcon, & .MuiButton-endIcon": {
              color: `${primaryFontColor} !important`,
            },
            "&.MuiButton-root, & .MuiButton-label": {
              color: `${primaryFontColor} !important`,
            },
          },
          sizeButtonSx,
          ...(buttonSx
            ? Array.isArray(buttonSx)
              ? buttonSx
              : [buttonSx]
            : []),
        ]}
      >
        Run AI Assessment
      </Button>
      {menu}
    </>
  );
};
